import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getUsers, inviteUser, updateUser, deleteUser, getUserMonthlyTime } from '../lib/api';
import { formatDate, formatCurrency, formatTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Shield, 
  ShieldCheck, 
  Eye, 
  UserPlus, 
  Users as UsersIcon,
  Settings,
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  Banknote,
  Timer
} from 'lucide-react';
import { toast } from 'sonner';

const permissionConfig = {
  clients: { label: 'Klienti', description: 'Přístup ke správě klientů a jejich detailům', icon: UsersIcon },
  projects: { label: 'Projekty', description: 'Správa projektů a jejich nastavení', icon: Settings },
  tasks: { label: 'Úkoly', description: 'Vytváření a správa úkolů', icon: CheckCircle },
  time_tracking: { label: 'Čas', description: 'Sledování času a záznamy', icon: Clock },
  revenue: { label: 'Tržby', description: 'Finanční data a odhady tržeb', icon: Mail },
  users: { label: 'Uživatelé', description: 'Správa uživatelů a oprávnění', icon: ShieldCheck },
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  
  // Invite form
  const [inviteData, setInviteData] = useState({
    email: '',
    name: '',
    password: '',
    hourly_rate: 300,
    monthly_capacity_hours: 160,
    permissions: {
      clients: false,
      projects: true,
      tasks: true,
      time_tracking: true,
      revenue: false,
      users: false,
    },
  });

  // Edit permissions form
  const [editPermissions, setEditPermissions] = useState({});
  const [editHourlyRate, setEditHourlyRate] = useState(300);
  const [editMonthlyCapacity, setEditMonthlyCapacity] = useState(160);
  const [monthlyTimes, setMonthlyTimes] = useState({});

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      const usersData = response.data;
      setUsers(usersData);
      
      // Fetch monthly time for each user
      const timesData = {};
      await Promise.all(
        usersData.map(async (u) => {
          try {
            const timeRes = await getUserMonthlyTime(u.id);
            timesData[u.id] = timeRes.data;
          } catch {
            timesData[u.id] = { total_minutes: 0 };
          }
        })
      );
      setMonthlyTimes(timesData);
    } catch (error) {
      toast.error('Chyba při načítání uživatelů');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      await inviteUser({
        ...inviteData,
        hourly_rate: parseFloat(inviteData.hourly_rate) || 300,
        monthly_capacity_hours: parseFloat(inviteData.monthly_capacity_hours) || 160,
      });
      toast.success('Uživatel byl pozván');
      setInviteDialogOpen(false);
      setInviteData({
        email: '',
        name: '',
        password: '',
        hourly_rate: 300,
        monthly_capacity_hours: 160,
        permissions: {
          clients: false,
          projects: true,
          tasks: true,
          time_tracking: true,
          revenue: false,
          users: false,
        },
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při vytváření pozvánky');
    }
  };

  const handleEditPermissions = async (e) => {
    e.preventDefault();
    try {
      await updateUser(editingUser.id, {
        name: editingUser.name,
        permissions: editPermissions,
        hourly_rate: parseFloat(editHourlyRate) || 300,
        monthly_capacity_hours: parseFloat(editMonthlyCapacity) || 160,
      });
      toast.success('Oprávnění byla aktualizována');
      setEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error('Chyba při aktualizaci oprávnění');
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setEditPermissions(user.permissions || {});
    setEditHourlyRate(user.hourly_rate || 300);
    setEditMonthlyCapacity(user.monthly_capacity_hours || 160);
    setEditDialogOpen(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Opravdu chcete smazat uživatele ${name}?`)) return;
    try {
      await deleteUser(id);
      toast.success('Uživatel byl smazán');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při mazání');
    }
  };

  // Quick permission templates
  const applyTemplate = (template) => {
    switch (template) {
      case 'employee':
        setInviteData(prev => ({
          ...prev,
          permissions: {
            clients: false,
            projects: true,
            tasks: true,
            time_tracking: true,
            revenue: false,
            users: false,
          },
        }));
        break;
      case 'manager':
        setInviteData(prev => ({
          ...prev,
          permissions: {
            clients: true,
            projects: true,
            tasks: true,
            time_tracking: true,
            revenue: false,
            users: false,
          },
        }));
        break;
      case 'admin':
        setInviteData(prev => ({
          ...prev,
          permissions: {
            clients: true,
            projects: true,
            tasks: true,
            time_tracking: true,
            revenue: true,
            users: true,
          },
        }));
        break;
      default:
        break;
    }
  };

  const adminUsers = users.filter(u => u.is_admin);
  const regularUsers = users.filter(u => !u.is_admin);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="font-oswald text-xl uppercase tracking-wide">Načítání...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-oswald text-4xl font-bold uppercase tracking-tight">Správa uživatelů</h1>
          <p className="font-serif text-gray-600 mt-1">
            Pozvánky, oprávnění a přístupy
          </p>
        </div>
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#F4E600] text-black font-oswald uppercase tracking-wide border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
              data-testid="invite-user-btn"
            >
              <UserPlus size={18} className="mr-2" />
              Pozvat uživatele
            </Button>
          </DialogTrigger>
          <DialogContent className="border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-oswald text-xl uppercase tracking-tight flex items-center gap-2">
                <UserPlus size={24} />
                Pozvat nového uživatele
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleInvite} className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="font-oswald text-sm uppercase tracking-wide text-gray-500 border-b-2 border-gray-200 pb-2">
                  Základní údaje
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-oswald uppercase text-xs tracking-wide">Jméno *</Label>
                    <Input
                      value={inviteData.name}
                      onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                      required
                      placeholder="Jan Novák"
                      className="border-2 border-gray-200 focus:border-black rounded-none"
                      data-testid="invite-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-oswald uppercase text-xs tracking-wide">Email *</Label>
                    <Input
                      type="email"
                      value={inviteData.email}
                      onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                      required
                      placeholder="jan@welead.cz"
                      className="border-2 border-gray-200 focus:border-black rounded-none"
                      data-testid="invite-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Heslo *</Label>
                  <Input
                    type="password"
                    value={inviteData.password}
                    onChange={(e) => setInviteData({ ...inviteData, password: e.target.value })}
                    required
                    minLength={6}
                    placeholder="Minimálně 6 znaků"
                    className="border-2 border-gray-200 focus:border-black rounded-none"
                    data-testid="invite-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Hodinová sazba (CZK)</Label>
                  <Input
                    type="number"
                    value={inviteData.hourly_rate}
                    onChange={(e) => setInviteData({ ...inviteData, hourly_rate: e.target.value })}
                    placeholder="300"
                    className="border-2 border-gray-200 focus:border-black rounded-none"
                    data-testid="invite-hourly-rate-input"
                  />
                  <p className="text-xs text-gray-500 font-serif">Výchozí: 300 Kč/h</p>
                </div>
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Měsíční kapacita (h)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="400"
                    value={inviteData.monthly_capacity_hours}
                    onChange={(e) => setInviteData({ ...inviteData, monthly_capacity_hours: e.target.value })}
                    placeholder="160"
                    className="border-2 border-gray-200 focus:border-black rounded-none"
                    data-testid="invite-monthly-capacity-input"
                  />
                  <p className="text-xs text-gray-500 font-serif">Výchozí: 160 h/měsíc (plný úvazek)</p>
                </div>
              </div>

              {/* Permission Templates */}
              <div className="space-y-4">
                <div className="font-oswald text-sm uppercase tracking-wide text-gray-500 border-b-2 border-gray-200 pb-2">
                  Šablony oprávnění
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => applyTemplate('employee')}
                    className="p-3 border-2 border-gray-200 hover:border-black text-center transition-colors"
                    data-testid="template-employee"
                  >
                    <div className="font-oswald text-sm uppercase">Zaměstnanec</div>
                    <div className="text-xs text-gray-500 font-serif mt-1">Projekty, Úkoly, Čas</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => applyTemplate('manager')}
                    className="p-3 border-2 border-gray-200 hover:border-black text-center transition-colors"
                    data-testid="template-manager"
                  >
                    <div className="font-oswald text-sm uppercase">Manažer</div>
                    <div className="text-xs text-gray-500 font-serif mt-1">+ Klienti</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => applyTemplate('admin')}
                    className="p-3 border-2 border-gray-200 hover:border-black text-center transition-colors"
                    data-testid="template-admin"
                  >
                    <div className="font-oswald text-sm uppercase">Administrátor</div>
                    <div className="text-xs text-gray-500 font-serif mt-1">Vše</div>
                  </button>
                </div>
              </div>

              {/* Permissions */}
              <div className="space-y-4">
                <div className="font-oswald text-sm uppercase tracking-wide text-gray-500 border-b-2 border-gray-200 pb-2">
                  Oprávnění
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {Object.entries(permissionConfig).map(([key, { label, description, icon: Icon }]) => (
                    <label
                      key={key}
                      htmlFor={`invite-perm-${key}`}
                      className={`flex items-center gap-4 p-4 border-2 cursor-pointer transition-all ${
                        inviteData.permissions[key] 
                          ? 'border-[#006CB6] bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <Checkbox
                        id={`invite-perm-${key}`}
                        checked={inviteData.permissions[key]}
                        onCheckedChange={(checked) => {
                          setInviteData(prev => ({
                            ...prev,
                            permissions: {
                              ...prev.permissions,
                              [key]: checked
                            }
                          }));
                        }}
                        className="border-2 border-black data-[state=checked]:bg-[#006CB6] data-[state=checked]:border-[#006CB6]"
                        data-testid={`invite-permission-${key}`}
                      />
                      <Icon size={20} className={inviteData.permissions[key] ? 'text-[#006CB6]' : 'text-gray-400'} />
                      <div className="flex-1">
                        <div className="font-oswald text-sm uppercase">{label}</div>
                        <div className="text-xs text-gray-500 font-serif">{description}</div>
                      </div>
                      {inviteData.permissions[key] ? (
                        <CheckCircle size={20} className="text-green-500" />
                      ) : (
                        <XCircle size={20} className="text-gray-300" />
                      )}
                    </label>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#F4E600] text-black font-oswald uppercase tracking-wide border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
                data-testid="invite-submit-btn"
              >
                <Mail size={18} className="mr-2" />
                Vytvořit účet a poslat pozvánku
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#F4E600] border-2 border-black">
              <UsersIcon size={24} />
            </div>
            <div>
              <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">Celkem uživatelů</div>
              <div className="font-oswald text-3xl font-bold">{users.length}</div>
            </div>
          </div>
        </div>
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#006CB6] border-2 border-black">
              <ShieldCheck size={24} className="text-white" />
            </div>
            <div>
              <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">Administrátoři</div>
              <div className="font-oswald text-3xl font-bold">{adminUsers.length}</div>
            </div>
          </div>
        </div>
        <div className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-black border-2 border-black">
              <Shield size={24} className="text-white" />
            </div>
            <div>
              <div className="font-oswald text-xs uppercase tracking-wide text-gray-500">Zaměstnanci</div>
              <div className="font-oswald text-3xl font-bold">{regularUsers.length}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Users Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-white border-2 border-black p-1 rounded-none">
          <TabsTrigger 
            value="all" 
            className="font-oswald uppercase text-sm data-[state=active]:bg-[#F4E600] data-[state=active]:text-black rounded-none px-6"
          >
            Všichni ({users.length})
          </TabsTrigger>
          <TabsTrigger 
            value="admins" 
            className="font-oswald uppercase text-sm data-[state=active]:bg-[#F4E600] data-[state=active]:text-black rounded-none px-6"
          >
            Administrátoři ({adminUsers.length})
          </TabsTrigger>
          <TabsTrigger 
            value="employees" 
            className="font-oswald uppercase text-sm data-[state=active]:bg-[#F4E600] data-[state=active]:text-black rounded-none px-6"
          >
            Zaměstnanci ({regularUsers.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          <UsersList 
            users={users} 
            onEdit={openEditDialog} 
            onDelete={handleDelete}
            monthlyTimes={monthlyTimes}
          />
        </TabsContent>
        <TabsContent value="admins" className="space-y-4">
          <UsersList 
            users={adminUsers} 
            onEdit={openEditDialog} 
            onDelete={handleDelete}
            monthlyTimes={monthlyTimes}
          />
        </TabsContent>
        <TabsContent value="employees" className="space-y-4">
          <UsersList 
            users={regularUsers} 
            onEdit={openEditDialog} 
            onDelete={handleDelete}
            monthlyTimes={monthlyTimes}
          />
        </TabsContent>
      </Tabs>

      {/* Edit Permissions Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-oswald text-xl uppercase tracking-tight flex items-center gap-2">
              <Settings size={24} />
              Upravit oprávnění: {editingUser?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditPermissions} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide">Hodinová sazba (CZK)</Label>
                <Input
                  type="number"
                  value={editHourlyRate}
                  onChange={(e) => setEditHourlyRate(e.target.value)}
                  placeholder="300"
                  className="border-2 border-gray-200 focus:border-black rounded-none"
                  data-testid="edit-hourly-rate-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide">Měsíční kapacita (h)</Label>
                <Input
                  type="number"
                  min="0"
                  max="400"
                  value={editMonthlyCapacity}
                  onChange={(e) => setEditMonthlyCapacity(e.target.value)}
                  placeholder="160"
                  className="border-2 border-gray-200 focus:border-black rounded-none"
                  data-testid="edit-monthly-capacity-input"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 font-serif">Kapacita se používá pro výpočet forecastu nákladů a využití kapacity týmu</p>
            <div className="grid grid-cols-1 gap-3">
              {Object.entries(permissionConfig).map(([key, { label, description, icon: Icon }]) => (
                <label
                  key={key}
                  htmlFor={`edit-perm-${key}`}
                  className={`flex items-center gap-4 p-4 border-2 cursor-pointer transition-all ${
                    editPermissions[key] 
                      ? 'border-[#006CB6] bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Checkbox
                    id={`edit-perm-${key}`}
                    checked={editPermissions[key] || false}
                    onCheckedChange={(checked) => {
                      setEditPermissions(prev => ({
                        ...prev,
                        [key]: checked
                      }));
                    }}
                    className="border-2 border-black data-[state=checked]:bg-[#006CB6] data-[state=checked]:border-[#006CB6]"
                  />
                  <Icon size={20} className={editPermissions[key] ? 'text-[#006CB6]' : 'text-gray-400'} />
                  <div className="flex-1">
                    <div className="font-oswald text-sm uppercase">{label}</div>
                    <div className="text-xs text-gray-500 font-serif">{description}</div>
                  </div>
                  {editPermissions[key] ? (
                    <CheckCircle size={20} className="text-green-500" />
                  ) : (
                    <XCircle size={20} className="text-gray-300" />
                  )}
                </label>
              ))}
            </div>
            <Button
              type="submit"
              className="w-full bg-[#F4E600] text-black font-oswald uppercase tracking-wide border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
            >
              Uložit změny
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Users List Component
const UsersList = ({ users, onEdit, onDelete, monthlyTimes }) => {
  if (users.length === 0) {
    return (
      <div className="bg-white border-2 border-black p-12 text-center shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
        <UsersIcon size={48} className="mx-auto text-gray-300 mb-4" />
        <p className="font-serif text-gray-500">Žádní uživatelé v této kategorii</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {users.map((user) => (
        <div
          key={user.id}
          className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
          data-testid={`user-card-${user.id}`}
        >
          <div className="p-4 flex items-center gap-4">
            {/* Avatar */}
            <div className={`w-12 h-12 flex items-center justify-center border-2 border-black ${user.is_admin ? 'bg-[#006CB6]' : 'bg-gray-100'}`}>
              {user.is_admin ? (
                <ShieldCheck size={24} className="text-white" />
              ) : (
                <Shield size={24} className="text-gray-600" />
              )}
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-oswald text-lg font-bold uppercase">{user.name}</span>
                <span className={`px-2 py-0.5 text-xs font-oswald uppercase ${user.is_admin ? 'bg-[#006CB6] text-white' : 'bg-gray-100 text-gray-600'}`}>
                  {user.is_admin ? 'Admin' : 'Uživatel'}
                </span>
              </div>
              <div className="font-serif text-sm text-gray-600">{user.email}</div>
              <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                <div className="flex items-center gap-1">
                  <Banknote size={14} />
                  <span className="font-serif">{formatCurrency(user.hourly_rate || 300)}/h</span>
                </div>
                <div className="flex items-center gap-1">
                  <Timer size={14} />
                  <span className="font-serif">{user.monthly_capacity_hours || 160}h/měs</span>
                </div>
                {monthlyTimes && monthlyTimes[user.id] && (
                  <div className="flex items-center gap-1 text-[#006CB6]">
                    <Clock size={14} />
                    <span className="font-serif font-medium">
                      {formatTime(monthlyTimes[user.id].total_minutes || 0)} tento měsíc
                    </span>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {Object.entries(user.permissions || {}).map(([key, value]) => (
                  <span
                    key={key}
                    className={`px-2 py-0.5 text-xs font-oswald uppercase border ${
                      value 
                        ? 'bg-green-50 text-green-700 border-green-200' 
                        : 'bg-gray-50 text-gray-400 border-gray-200 line-through'
                    }`}
                  >
                    {permissionConfig[key]?.label || key}
                  </span>
                ))}
              </div>
            </div>

            {/* Created date */}
            <div className="hidden md:block text-right">
              <div className="font-oswald text-xs uppercase text-gray-500">Vytvořeno</div>
              <div className="font-serif text-sm">{formatDate(user.created_at)}</div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <Link
                to={`/users/${user.id}`}
                className="p-2 hover:bg-gray-100 border-2 border-gray-200"
                data-testid={`view-user-${user.id}`}
                title="Zobrazit detail"
              >
                <Eye size={18} />
              </Link>
              <button
                onClick={() => onEdit(user)}
                className="p-2 hover:bg-gray-100 border-2 border-gray-200 disabled:opacity-50"
                data-testid={`edit-user-${user.id}`}
                disabled={user.is_admin}
                title="Upravit oprávnění"
              >
                <Settings size={18} className={user.is_admin ? 'text-gray-300' : ''} />
              </button>
              <button
                onClick={() => onDelete(user.id, user.name)}
                className="p-2 hover:bg-red-50 border-2 border-gray-200 text-red-600 disabled:opacity-50"
                data-testid={`delete-user-${user.id}`}
                disabled={user.is_admin}
                title="Smazat uživatele"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default Users;
